import type { RemovalPlan } from '@anytracker/core'

import { Button } from '../../components/Button.js'
import { Sheet } from '../../components/Sheet.js'
import styles from './TrackList.module.css'

interface Props {
  readonly plan: RemovalPlan | null
  readonly onCancel: () => void
  readonly onConfirm: () => void
  /** The way out that changes nothing, for someone who opened this by mistake. */
  readonly onEdit: () => void
}

/**
 * Says what will actually happen, before it happens.
 *
 * This dialog is the whole of T-301. A prompt saying "Delete?" that then
 * archives is a broken promise; one saying "this has 412 measurements, so it
 * will be archived and its history kept" is the app being trustworthy at
 * exactly the moment trust matters.
 *
 * Both the wording and the button label come from the **plan** — the same value
 * `remove()` acts on — so the dialog cannot describe one outcome while the
 * repository performs another (ADR-0003, and the rule from T-107).
 */
export function RemoveTrackDialog({ plan, onCancel, onConfirm, onEdit }: Props) {
  if (!plan) {
    return null
  }

  const permanent = plan.kind === 'delete'

  return (
    <Sheet
      open
      title={permanent ? `Delete ${plan.track.name}?` : `Archive ${plan.track.name}?`}
      // No corner control: this sheet asks a question and offers Cancel below,
      // so an X would be a second control with the same job and no better name
      // for it. The scrim and Escape are still there.
      dismissLabel={null}
      onClose={onCancel}
    >
      <div className={styles['confirm']}>
        {permanent ? (
          <p>
            Nothing has ever been recorded against <strong>{plan.track.name}</strong>, so
            it will be deleted permanently. This cannot be undone.
          </p>
        ) : (
          <>
            <p>
              <strong>{plan.track.name}</strong> has{' '}
              {plan.measurementCount === 1
                ? '1 measurement'
                : `${String(plan.measurementCount)} measurements`}{' '}
              recorded against it, so it will be <strong>archived</strong> rather than
              deleted.
            </p>
            <p>
              Every one of those measurements is kept and goes on appearing in your
              timeline and charts. The track stops being offered when you log something,
              and you can restore it at any time.
            </p>
          </>
        )}

        <div className={styles['confirmActions']}>
          {/*
            Offered here because this is where somebody arrives having decided a
            track is wrong, and "wrong" is more often a name or a range than the
            whole track. Reaching the fix by cancelling and finding another
            control is the version of this that technically works.
          */}
          <Button onClick={onEdit}>Edit instead</Button>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="danger" onClick={onConfirm}>
            {permanent ? 'Delete permanently' : 'Archive'}
          </Button>
        </div>
      </div>
    </Sheet>
  )
}
